import { useEffect } from 'react'

import mixpanel from 'mixpanel-browser'
import type { Mixpanel } from 'mixpanel-browser'

import { isRealProduction } from '~publish/env'

/** Flag to track if Mixpanel has been initialized */
let isMixpanelInitialized = false

/**
 * Check if Mixpanel has been initialized and is ready for use.
 * Used by other hooks to wait for Mixpanel SDK to be ready before calling its methods.
 * @returns true if Mixpanel's `loaded` callback has fired, false otherwise.
 */
export function isMixpanelReady(): boolean {
  return isMixpanelInitialized
}

type SegmentPayload = {
  obj: {
    type: string
    anonymousId?: string
    userId?: string
    properties?: Record<string, unknown>
  }
}

type SegmentNext = (payload: SegmentPayload) => void

export function mixpanelSegmentMiddleware(mixpanelClient: Mixpanel): void {
  // Middleware to add Mixpanel's session recording properties to Segment events
  // https://docs.mixpanel.com/docs/session-replay/session-replay-web#segment-analyticsjs
  if (window?.analytics?.addSourceMiddleware) {
    window.analytics.addSourceMiddleware(
      ({ payload, next }: { payload: SegmentPayload; next: SegmentNext }) => {
        if (payload.obj.type === 'track' || payload.obj.type === 'page') {
          if (mixpanelClient) {
            const segmentDeviceId = payload.obj.anonymousId
            // -------------------------------------------
            // Comment out one of the below mixpanel.register methods depending on your ID Management Version
            // Original ID Merge
            mixpanelClient.register({
              $device_id: segmentDeviceId,
              distinct_id: segmentDeviceId,
            })
            // Simplified ID Merge
            // mixpanel.register({
            //   $device_id: segmentDeviceId,
            //   distinct_id: '$device:' + segmentDeviceId,
            // })
            // -------------------------------------------
            const sessionReplayProperties =
              mixpanelClient.get_session_recording_properties()
            payload.obj.properties = {
              ...payload.obj.properties,
              ...sessionReplayProperties,
            }
          }
        }

        if (payload.obj.type === 'identify') {
          if (mixpanelClient && payload.obj.userId) {
            const userId = payload.obj.userId
            mixpanelClient.identify(userId)
          }
        }
        next(payload)
      },
    )
  }
}

export function useMixpanelSessionReplay(): void {
  useEffect(() => {
    if (isRealProduction()) {
      const mixpanelProjectToken = '90f73cfa0608e74231d66fdf4f31e120'
      mixpanelSegmentMiddleware(mixpanel)
      const mixpanelConfig: Parameters<Mixpanel['init']>[1] & {
        record_min_ms: number
      } = {
        record_mask_text_selector: '',
        record_block_selector: '',
        record_sessions_percent: 0.5,
        record_min_ms: 5000,
        loaded: () => {
          isMixpanelInitialized = true
        },
      }
      mixpanel.init(mixpanelProjectToken, mixpanelConfig)
    }
  }, [])
}
